import {
  FUEL_PAYMENT_TYPE_LABELS,
  FUEL_PAYMENT_TYPES,
  FUEL_TRANSACTION_SOURCE_LABELS,
  type FuelPaymentType,
  fuelPaymentTypeSchema,
} from "@station/contracts";
import { addDays, type LocalDate, toIstanbulDate, istanbulDayRange } from "@station/core";
import {
  Alert,
  Card,
  Checkbox,
  DataTable,
  type DataTableColumn,
  DateInput,
  DateRangePicker,
  EmptyState,
  FormField,
  MoneyText,
  QuantityText,
  Select,
  SkeletonRows,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@station/ui";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Section } from "../../../features/expenses/shared.js";
import { hourlyLitresChartOption, stackedLitresChartOption } from "../../../features/fuel/chart-options.js";
import {
  fetchFuelTransactionPage,
  useFuelPermissions,
  useFuelSalesDaily,
  useFuelSalesHourly,
  useFuelTransactions,
  useStationAssets,
  useTanks,
} from "../../../features/fuel/queries.js";
import {
  dailyLitresSeries,
  hourlyLitresSeries,
  type ProductInfo,
  salesTotals,
  sumPaymentBreakdowns,
} from "../../../features/fuel/sales-values.js";
import {
  ChartWithTable,
  FuelPage,
  fuelSearchShape,
  NoStations,
  StationField,
  Th,
  useFuelStation,
} from "../../../features/fuel/shared.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../../lib/excel.js";
import { formatCount, formatDate, formatDateTime, formatUnitPrice } from "../../../lib/format.js";

const SALES_TABS = ["daily", "hourly", "transactions"] as const;
type SalesTab = (typeof SALES_TABS)[number];
const localDateText = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);

const searchSchema = z.object({
  ...fuelSearchShape,
  tab: z.enum(SALES_TABS).optional().catch(undefined),
  from: localDateText,
  to: localDateText,
  day: localDateText,
  nozzleId: z.string().uuid().optional().catch(undefined),
  productId: z.string().uuid().optional().catch(undefined),
  paymentType: fuelPaymentTypeSchema.optional().catch(undefined),
  onlyTest: z.enum(["true"]).optional().catch(undefined),
});
type SalesSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_app/akaryakit/satislar")({
  validateSearch: (search) => searchSchema.parse(search),
  component: SalesPage,
});

function SalesPage(): ReactElement {
  const { t } = useTranslation();
  const permissions = useFuelPermissions();
  return (
    <FuelPage
      title={t("menu.fuelSales")}
      description={t("fuel.sales.description")}
      breadcrumb={[{ label: t("menu.fuelSales") }]}
    >
      {({ companyId }) =>
        permissions.canRead ? (
          <SalesBody companyId={companyId} />
        ) : (
          <Alert tone="info">{t("fuel.noPermission")}</Alert>
        )
      }
    </FuelPage>
  );
}

/** Ürün adı / rengi tank durumundan çözülür: istasyonun akaryakıt ürünleri tanklara bağlıdır. */
function useStationProducts(stationId: string | null): ProductInfo[] {
  const tanks = useTanks(stationId);
  return useMemo(() => {
    const products = new Map<string, ProductInfo>();
    for (const tank of tanks.data?.items ?? [])
      products.set(tank.productId, {
        id: tank.productId,
        name: tank.productName,
        color: tank.productColor,
      });
    return [...products.values()];
  }, [tanks.data]);
}

function SalesBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const station = useFuelStation(companyId);
  const products = useStationProducts(station.stationId);
  const tab: SalesTab = search.tab ?? "daily";

  const setSearch = (patch: Partial<SalesSearch>) =>
    void navigate({ search: (previous) => ({ ...previous, ...patch }), replace: true });

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stationId === null) return <NoStations />;
  const stationId = station.stationId;

  return (
    <Tabs className="grid gap-4" value={tab} onValueChange={(next) => setSearch({ tab: next as SalesTab })}>
      <Card className="flex flex-wrap items-end justify-between gap-3 p-4">
        <StationField selection={station} />
        <TabsList aria-label={t("fuel.sales.tabs")}>
          <TabsTrigger value="daily">{t("fuel.sales.dailyTab")}</TabsTrigger>
          <TabsTrigger value="hourly">{t("fuel.sales.hourlyTab")}</TabsTrigger>
          <TabsTrigger value="transactions">{t("fuel.sales.transactionsTab")}</TabsTrigger>
        </TabsList>
      </Card>

      {/* Etkin sekmenin paneli: tetikleyicinin `aria-controls` hedefi var olmalı (axe: aria-valid-attr-value). */}
      <TabsContent value={tab} className="grid gap-4 pt-0">
        {tab === "daily" ? (
          <DailyTab stationId={stationId} products={products} search={search} setSearch={setSearch} />
        ) : tab === "hourly" ? (
          <HourlyTab stationId={stationId} products={products} search={search} setSearch={setSearch} />
        ) : (
          <TransactionsTab stationId={stationId} products={products} search={search} setSearch={setSearch} />
        )}
      </TabsContent>
    </Tabs>
  );
}

function defaultRange(): { from: LocalDate; to: LocalDate } {
  const today = toIstanbulDate(new Date());
  return { from: addDays(today, -6), to: today };
}

function DailyTab({
  stationId,
  products,
  search,
  setSearch,
}: {
  stationId: string;
  products: ProductInfo[];
  search: SalesSearch;
  setSearch: (patch: Partial<SalesSearch>) => void;
}): ReactElement {
  const { t } = useTranslation();
  const fallback = defaultRange();
  const range = { from: search.from ?? fallback.from, to: search.to ?? fallback.to };
  const daily = useFuelSalesDaily(stationId, range);
  const rows = useMemo(() => daily.data?.items ?? [], [daily.data]);
  const { dates, series } = useMemo(() => dailyLitresSeries(rows, products), [rows, products]);
  const totals = useMemo(() => salesTotals(rows), [rows]);
  const payments = useMemo(() => sumPaymentBreakdowns(rows.map((row) => row.byPaymentType)), [rows]);
  const option = useMemo(
    () =>
      stackedLitresChartOption(
        dates.map((date) => formatDate(date)),
        series,
        { litresAxis: t("fuel.sales.litresAxis") },
      ),
    [dates, series, t],
  );

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <DateRangePicker
          label={t("fuel.sales.businessDate")}
          value={{ from: range.from as LocalDate, to: range.to as LocalDate }}
          onValueChange={(next) => setSearch({ from: next.from ?? undefined, to: next.to ?? undefined })}
        />
      </Card>

      {daily.error ? (
        <Alert tone="danger">{errorMessage(daily.error)}</Alert>
      ) : !daily.data ? (
        <SkeletonRows rows={6} />
      ) : rows.length === 0 ? (
        <EmptyState title={t("fuel.sales.empty")} description={t("fuel.sales.emptyHint")} />
      ) : (
        <>
          <Section title={t("fuel.sales.dailyChartTitle")}>
            <ChartWithTable
              ariaLabel={t("fuel.sales.dailyChartAria", {
                from: formatDate(range.from),
                to: formatDate(range.to),
              })}
              option={option}
              tableLabel={t("fuel.sales.dailyTableLabel")}
            >
              <DailyTable rows={rows} products={products} />
            </ChartWithTable>
          </Section>

          <Section title={t("fuel.sales.totals")}>
            <dl className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
              <SummaryField label={t("fuel.sales.fields.litres")}>
                <QuantityText value={totals.litres} unit="LITRE" />
              </SummaryField>
              <SummaryField label={t("fuel.sales.fields.amount")}>
                <MoneyText value={totals.amountMinor} />
              </SummaryField>
              <SummaryField label={t("fuel.sales.fields.transactionCount")}>
                {formatCount(totals.transactionCount)}
              </SummaryField>
              <SummaryField label={t("fuel.sales.fields.testLitres")}>
                <QuantityText value={totals.testLitres} unit="LITRE" />
              </SummaryField>
            </dl>
            <div
              className="overflow-x-auto rounded-lg border"
              tabIndex={0}
              role="group"
              aria-label={t("fuel.sales.paymentTable")}
            >
              <table className="w-full text-sm">
                <caption className="sr-only">{t("fuel.sales.paymentTable")}</caption>
                <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
                  <tr>
                    <Th>{t("fuel.sales.fields.paymentType")}</Th>
                    <Th align="right">{t("fuel.sales.fields.litres")}</Th>
                    <Th align="right">{t("fuel.sales.fields.amount")}</Th>
                    <Th align="right">{t("fuel.sales.fields.transactionCount")}</Th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.paymentType} className="border-t">
                      <td className="px-3 py-2">{FUEL_PAYMENT_TYPE_LABELS[payment.paymentType]}</td>
                      <td className="px-3 py-2 text-right">
                        <QuantityText value={payment.litres} unit="LITRE" />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <MoneyText value={payment.amountMinor} />
                      </td>
                      <td className="px-3 py-2 text-right">{formatCount(payment.transactionCount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        </>
      )}
    </>
  );
}

function SummaryField({ label, children }: { label: string; children: ReactElement | string }): ReactElement {
  return (
    <div className="grid gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{children}</dd>
    </div>
  );
}

type DailyRow = {
  productId: string;
  businessDate: string;
  litres: string;
  amount: string;
  transactionCount: number;
  testLitres: string;
};

function DailyTable({
  rows,
  products,
}: {
  rows: readonly DailyRow[];
  products: readonly ProductInfo[];
}): ReactElement {
  const { t } = useTranslation();
  const ordered = [...rows].sort(
    (left, right) =>
      right.businessDate.localeCompare(left.businessDate) || left.productId.localeCompare(right.productId),
  );
  const productName = (productId: string): string =>
    products.find((product) => product.id === productId)?.name ?? productId;
  return (
    <table className="w-full text-sm">
      <caption className="sr-only">{t("fuel.sales.dailyTableLabel")}</caption>
      <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
        <tr>
          <Th>{t("fuel.sales.fields.businessDate")}</Th>
          <Th>{t("fuel.sales.fields.product")}</Th>
          <Th align="right">{t("fuel.sales.fields.litres")}</Th>
          <Th align="right">{t("fuel.sales.fields.amount")}</Th>
          <Th align="right">{t("fuel.sales.fields.transactionCount")}</Th>
          <Th align="right">{t("fuel.sales.fields.testLitres")}</Th>
        </tr>
      </thead>
      <tbody>
        {ordered.map((row) => (
          <tr key={`${row.businessDate}:${row.productId}`} className="border-t">
            <td className="px-3 py-2">{formatDate(row.businessDate)}</td>
            <td className="px-3 py-2">{productName(row.productId)}</td>
            <td className="px-3 py-2 text-right">
              <QuantityText value={row.litres} unit="LITRE" />
            </td>
            <td className="px-3 py-2 text-right">
              <MoneyText value={row.amount} />
            </td>
            <td className="px-3 py-2 text-right">{formatCount(row.transactionCount)}</td>
            <td className="px-3 py-2 text-right">
              <QuantityText value={row.testLitres} unit="LITRE" />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const HOUR_FORMAT = new Intl.DateTimeFormat("tr-TR", {
  timeZone: "Europe/Istanbul",
  hour: "2-digit",
  minute: "2-digit",
});

function HourlyTab({
  stationId,
  products,
  search,
  setSearch,
}: {
  stationId: string;
  products: ProductInfo[];
  search: SalesSearch;
  setSearch: (patch: Partial<SalesSearch>) => void;
}): ReactElement {
  const { t } = useTranslation();
  const day = (search.day ?? toIstanbulDate(new Date())) as LocalDate;
  const window = useMemo(() => {
    const { start, end } = istanbulDayRange(day);
    return { from: start.toISOString(), to: end.toISOString() };
  }, [day]);
  const hourly = useFuelSalesHourly(stationId, window);
  const rows = useMemo(() => hourly.data?.items ?? [], [hourly.data]);
  const { buckets, series } = useMemo(() => hourlyLitresSeries(rows, products), [rows, products]);
  const categories = useMemo(() => buckets.map((bucket) => HOUR_FORMAT.format(new Date(bucket))), [buckets]);
  const option = useMemo(
    () => hourlyLitresChartOption(categories, series, { litresAxis: t("fuel.sales.litresAxis") }),
    [categories, series, t],
  );

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <FormField label={t("fuel.sales.day")} className="w-48">
          {(control) => (
            <DateInput
              {...control}
              value={day}
              onValueChange={(next) => setSearch({ day: next ?? undefined })}
            />
          )}
        </FormField>
      </Card>

      {hourly.error ? (
        <Alert tone="danger">{errorMessage(hourly.error)}</Alert>
      ) : !hourly.data ? (
        <SkeletonRows rows={6} />
      ) : rows.length === 0 ? (
        <EmptyState title={t("fuel.sales.hourlyEmpty")} description={t("fuel.sales.hourlyEmptyHint")} />
      ) : (
        <Section title={t("fuel.sales.hourlyChartTitle", { date: formatDate(day) })}>
          <ChartWithTable
            ariaLabel={t("fuel.sales.hourlyChartAria", { date: formatDate(day) })}
            option={option}
            tableLabel={t("fuel.sales.hourlyTableLabel")}
          >
            <table className="w-full text-sm">
              <caption className="sr-only">{t("fuel.sales.hourlyTableLabel")}</caption>
              <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
                <tr>
                  <Th>{t("fuel.sales.fields.hour")}</Th>
                  {series.map((entry) => (
                    <th key={entry.productId} scope="col" className="px-3 py-2 text-right">
                      {entry.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {categories.map((category, index) => (
                  <tr key={buckets[index]} className="border-t">
                    <td className="px-3 py-2">{category}</td>
                    {series.map((entry) => (
                      <td key={entry.productId} className="px-3 py-2 text-right">
                        <QuantityText value={entry.values[index] ?? "0"} unit="LITRE" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </ChartWithTable>
        </Section>
      )}
    </>
  );
}

type TransactionRow = {
  id: string;
  occurredAt: string;
  businessDate: string;
  nozzleId: string;
  productId: string;
  litres: string;
  unitPrice: string;
  amount: string;
  paymentType: FuelPaymentType;
  vehiclePlate: string | null;
  isTest: boolean;
  source: keyof typeof FUEL_TRANSACTION_SOURCE_LABELS;
};

function TransactionsTab({
  stationId,
  products,
  search,
  setSearch,
}: {
  stationId: string;
  products: ProductInfo[];
  search: SalesSearch;
  setSearch: (patch: Partial<SalesSearch>) => void;
}): ReactElement {
  const { t } = useTranslation();
  const assets = useStationAssets(stationId);
  const [includePlateInExport, setIncludePlateInExport] = useState(false);

  const filters = {
    from: search.from ? new Date(`${search.from}T00:00:00`).toISOString() : undefined,
    to: search.to ? new Date(`${search.to}T23:59:59`).toISOString() : undefined,
    nozzleId: search.nozzleId,
    productId: search.productId,
    paymentType: search.paymentType,
    isTest: search.onlyTest === "true" ? true : undefined,
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useFuelTransactions(stationId, filters, pages.cursor);

  const nozzles = useMemo(
    () =>
      (assets.data?.dispensers ?? []).flatMap((dispenser) =>
        dispenser.nozzles.map((nozzle) => ({
          id: nozzle.id,
          label: t("fuel.totalizers.nozzleLabel", {
            dispenserNo: dispenser.dispenserNo,
            side: nozzle.side,
            nozzleNo: nozzle.nozzleNo,
            product: nozzle.productName,
          }),
        })),
      ),
    [assets.data, t],
  );
  const nozzleLabel = (nozzleId: string): string =>
    nozzles.find((nozzle) => nozzle.id === nozzleId)?.label ?? nozzleId;
  const productName = (productId: string): string =>
    products.find((product) => product.id === productId)?.name ?? productId;

  const columns: DataTableColumn<TransactionRow>[] = useMemo(
    () => [
      {
        id: "occurredAt",
        header: t("fuel.sales.fields.occurredAt"),
        cell: (row) => formatDateTime(row.occurredAt),
        exportValue: (row) => formatDateTime(row.occurredAt),
        hideable: false,
      },
      {
        id: "businessDate",
        header: t("fuel.sales.fields.businessDate"),
        cell: (row) => formatDate(row.businessDate),
        exportValue: (row) => formatDate(row.businessDate),
      },
      {
        id: "nozzle",
        header: t("fuel.sales.fields.nozzle"),
        cell: (row) => nozzleLabel(row.nozzleId),
        exportValue: (row) => nozzleLabel(row.nozzleId),
      },
      {
        id: "product",
        header: t("fuel.sales.fields.product"),
        cell: (row) => productName(row.productId),
        exportValue: (row) => productName(row.productId),
      },
      {
        id: "litres",
        header: t("fuel.sales.fields.litres"),
        align: "right",
        cell: (row) => <QuantityText value={row.litres} unit="LITRE" />,
        exportValue: (row) => row.litres,
      },
      {
        id: "unitPrice",
        header: t("fuel.sales.fields.unitPrice"),
        align: "right",
        cell: (row) => formatUnitPrice(row.unitPrice),
        exportValue: (row) => row.unitPrice,
      },
      {
        id: "amount",
        header: t("fuel.sales.fields.amount"),
        align: "right",
        cell: (row) => <MoneyText value={row.amount} />,
        exportValue: (row) => row.amount,
      },
      {
        id: "paymentType",
        header: t("fuel.sales.fields.paymentType"),
        cell: (row) => FUEL_PAYMENT_TYPE_LABELS[row.paymentType],
        exportValue: (row) => FUEL_PAYMENT_TYPE_LABELS[row.paymentType],
      },
      // KVKK: plaka kişisel veridir. Tabloda görünür ama dışa aktarıma VARSAYILAN olarak girmez.
      {
        id: "plate",
        header: t("fuel.sales.fields.plate"),
        cell: (row) => row.vehiclePlate ?? "—",
        ...(includePlateInExport ? { exportValue: (row: TransactionRow) => row.vehiclePlate ?? "" } : {}),
      },
      {
        id: "isTest",
        header: t("fuel.sales.fields.isTest"),
        cell: (row) => (row.isTest ? <StatusBadge tone="warning">{t("fuel.sales.test")}</StatusBadge> : null),
        exportValue: (row) => (row.isTest ? t("common.yes") : t("common.no")),
      },
      {
        id: "source",
        header: t("fuel.sales.fields.source"),
        defaultVisible: false,
        cell: (row) => FUEL_TRANSACTION_SOURCE_LABELS[row.source],
        exportValue: (row) => FUEL_TRANSACTION_SOURCE_LABELS[row.source],
      },
    ],
    // `nozzleLabel` / `productName` her render'da yeniden kurulur; bağımlılık kaynak verilerdir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, nozzles, products, includePlateInExport],
  );

  return (
    <DataTable<TransactionRow>
      caption={t("fuel.sales.transactionsTab")}
      columns={columns}
      rows={(list.data?.items ?? []) as TransactionRow[]}
      rowKey={(row) => row.id}
      loading={list.isPending}
      error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
      toolbar={
        <label className="flex min-h-6 items-center gap-2 text-sm">
          <Checkbox
            checked={includePlateInExport}
            onChange={(event) => setIncludePlateInExport(event.target.checked)}
          />
          {t("fuel.sales.includePlate")}
        </label>
      }
      filters={
        <>
          <DateRangePicker
            label={t("fuel.sales.occurredAtRange")}
            value={{
              from: (search.from ?? null) as LocalDate | null,
              to: (search.to ?? null) as LocalDate | null,
            }}
            onValueChange={(next) => setSearch({ from: next.from ?? undefined, to: next.to ?? undefined })}
          />
          <FormField label={t("fuel.sales.fields.nozzle")} className="w-56">
            {(control) => (
              <Select
                {...control}
                value={search.nozzleId ?? ""}
                onChange={(event) => setSearch({ nozzleId: event.target.value || undefined })}
              >
                <option value="">{t("common.all")}</option>
                {nozzles.map((nozzle) => (
                  <option key={nozzle.id} value={nozzle.id}>
                    {nozzle.label}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("fuel.sales.fields.product")} className="w-44">
            {(control) => (
              <Select
                {...control}
                value={search.productId ?? ""}
                onChange={(event) => setSearch({ productId: event.target.value || undefined })}
              >
                <option value="">{t("common.all")}</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("fuel.sales.fields.paymentType")} className="w-44">
            {(control) => (
              <Select
                {...control}
                value={search.paymentType ?? ""}
                onChange={(event) =>
                  setSearch({ paymentType: (event.target.value || undefined) as FuelPaymentType })
                }
              >
                <option value="">{t("common.all")}</option>
                {FUEL_PAYMENT_TYPES.map((paymentType) => (
                  <option key={paymentType} value={paymentType}>
                    {FUEL_PAYMENT_TYPE_LABELS[paymentType]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <label className="flex min-h-6 items-center gap-2 pb-2 text-sm">
            <Checkbox
              checked={search.onlyTest === "true"}
              onChange={(event) => setSearch({ onlyTest: event.target.checked ? "true" : undefined })}
            />
            {t("fuel.sales.onlyTest")}
          </label>
        </>
      }
      onExport={async (visibleColumns) => {
        const rows = await fetchAllPages<TransactionRow>(
          (cursor) =>
            fetchFuelTransactionPage(stationId, filters, cursor) as Promise<{
              items: TransactionRow[];
              page: { nextCursor: string | null; hasMore: boolean };
            }>,
        );
        await exportRowsToExcel({
          fileName: t("fuel.sales.exportFileName"),
          sheetName: t("fuel.sales.transactionsTab"),
          columns: visibleColumns,
          rows,
        });
      }}
      pagination={{
        hasPrevious: pages.hasPrevious,
        hasNext: Boolean(list.data?.page.hasMore),
        onPrevious: pages.previous,
        onNext: () => pages.next(list.data?.page.nextCursor),
        pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
      }}
    />
  );
}
